import React, { useState } from 'react';
import {
  AlertTriangle,
  Award,
  BarChart3,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  Layers,
  UserCheck,
  Users,
} from 'lucide-react';

interface AssessmentComponent {
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  details: string[];
}

export const PTAssessmentPage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>('General Report');

  const departmentComponents: AssessmentComponent[] = [
    {
      name: 'Logbook',
      description: 'Your weekly reports combined into one component.',
      icon: FileText,
      details: [
        'Shows your weekly work and daily activities clearly.',
        'Explains your main job with detailed steps and supporting diagrams where needed.',
        'Demonstrates technical writing, consistency, and learning growth.',
      ],
    },
    {
      name: 'Company Training Instructor Assessment',
      description: 'Assessment conducted by the training instructor at your company.',
      icon: UserCheck,
      details: [
        'The training instructor evaluates your performance throughout PT.',
        'Based on daily activities, learning progress, and technical contribution.',
        'Considers conduct, punctuality, attendance, and adherence to company procedures.',
      ],
    },
    {
      name: 'Supervisor Assessment',
      description: 'Assessment from your university supervisor during PT visits.',
      icon: Users,
      details: [
        'Supervisor usually visits at least once, often from around week 5 onwards.',
        'They may review your weekly reports and progress at the workplace.',
        'Focus is on outcomes, training quality, and your engagement.',
      ],
    },
  ];

  const generalReport: AssessmentComponent = {
    name: 'General Report',
    description: 'Your major technical report covering both the company and the process.',
    icon: FileSpreadsheet,
    details: [
      'Has two key parts: The Company and The Process.',
      'Must be signed by you and countersigned by the training instructor.',
      'The balance between the company and process parts changes depending on PT1, PT2, or PT3.',
      'Should include technical explanation, diagrams, analysis, and recommendations.',
    ],
  };

  const penalties = [
    'Late arrival note submission',
    'Late general report submission',
    'Late logbook submission',
  ];

  const toggleSection = (sectionName: string) => {
    setExpandedSection(expandedSection === sectionName ? null : sectionName);
  };

  const renderComponent = (component: AssessmentComponent) => {
    const Icon = component.icon;
    const isExpanded = expandedSection === component.name;

    return (
      <div
        key={component.name}
        className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm transition-colors hover:border-orange-200"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">{component.name}</h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">{component.description}</p>
            </div>
          </div>

          <button
            onClick={() => toggleSection(component.name)}
            className="shrink-0 rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>

        {isExpanded && (
          <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
            <div>
              <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Key requirements
              </h4>
              <ul className="space-y-2">
                {component.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400"></span>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-6xl p-4 lg:p-6">
      <div className="mb-6 rounded-3xl border border-orange-100 bg-gradient-to-br from-white to-orange-50/60 p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
            <Award className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 lg:text-3xl">PT Assessment Guide</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600 lg:text-base">
              A simplified guide to how your PT work is assessed, what matters most, and what to prepare.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
            <Layers className="h-5 w-5" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Department-Based</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">40%</p>
          <p className="mt-1 text-xs text-gray-600">Logbook, training instructor, and supervisor assessment.</p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">General Report</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">60%</p>
          <p className="mt-1 text-xs text-gray-600">The same for every department.</p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
            <Clock className="h-5 w-5" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Submission Timing</p>
          <p className="mt-1 text-sm font-semibold text-gray-900">Second week Friday</p>
          <p className="mt-1 text-sm text-gray-600">After the university reopens.</p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Late Penalty</p>
          <p className="mt-1 text-sm font-semibold text-gray-900">A deduction applies</p>
          <p className="mt-1 text-sm text-gray-600">For each late submission item.</p>
        </div>
      </div>

      <section className="mb-6">
        <div className="mb-3 rounded-3xl border border-orange-100 bg-gradient-to-br from-white to-orange-50/60 p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Department-Based Assessment</h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-600">
                  Covers your logbook, company training instructor assessment, and supervisor assessment.
                </p>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700">
              40%
            </span>
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-2xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              How this 40% is shared between the logbook, the training instructor, and the supervisor is set by your
              department, so the distribution is not the same for every department.
            </span>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {departmentComponents.map(renderComponent)}
        </div>
      </section>

      <section className="mb-6">
        <div className="rounded-3xl border border-emerald-200 bg-white p-5 shadow-sm transition-colors hover:border-emerald-300">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <FileSpreadsheet className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">General Report</h3>
                <p className="mt-1 text-sm leading-6 text-gray-600">{generalReport.description}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700">60%</span>
              <button
                onClick={() => toggleSection(generalReport.name)}
                className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label={expandedSection === generalReport.name ? 'Collapse section' : 'Expand section'}
              >
                {expandedSection === generalReport.name ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>The General Report carries 60% of the PT assessment and is the same for all departments.</span>
          </div>

          {expandedSection === generalReport.name && (
            <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
              <div>
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Key requirements
                </h4>
                <ul className="space-y-2">
                  {generalReport.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400"></span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <BarChart3 className="h-4 w-4 text-orange-500" />
                  PT level expectations
                </h3>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-gray-200 bg-white p-3">
                    <p className="text-sm font-semibold text-gray-900">PT1</p>
                    <p className="mt-1 text-xs text-gray-600">Stronger company section, basic process analysis.</p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-white p-3">
                    <p className="text-sm font-semibold text-gray-900">PT2</p>
                    <p className="mt-1 text-xs text-gray-600">Balanced company and process focus.</p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-white p-3">
                    <p className="text-sm font-semibold text-gray-900">PT3</p>
                    <p className="mt-1 text-xs text-gray-600">Process-heavy with stronger engineering analysis.</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-3 text-sm text-gray-700">
                  Use proper technical language, clear diagrams, and practical recommendations. Higher PT levels require
                  deeper analysis and stronger justification.
                </div>
              </div>

              <button className="inline-flex items-center gap-2 rounded-2xl bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700">
                <Download className="h-4 w-4" />
                Download example
              </button>
            </div>
          )}
        </div>
      </section>

      <div className="mt-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
            <Info className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Important notes</h2>
            <p className="text-sm text-gray-600">Keep these practical reminders in mind.</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-900">Submission deadline</p>
            <p className="mt-1 text-sm text-gray-700">All reports should be submitted by the second week Friday after reopening.</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-900">Logbook expectations</p>
            <p className="mt-1 text-sm text-gray-700">Include daily work, main job steps, and supporting diagrams where needed.</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Late submission penalties
          </h3>
          <ul className="space-y-1 text-sm text-amber-700">
            {penalties.map((penalty) => (
              <li key={penalty}>• {penalty}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
